import { useEffect, useState, type FormEvent } from "react";

import { Alert, Badge, Button, Card, CardTitle, Chip, ChipGroup, Input, Label, NativeSelect } from "@realroof/ui";

import {
  ACTION_LABEL,
  AD_MODULE_LABEL,
  api,
  detailText,
  LEVEL_LABEL,
  MODULE_LABEL,
  ROLE_LABEL,
  ROLE_MODULES,
  type Activity,
  type AdModule,
  type Level,
  type Module,
  type StaffMember,
  type StaffRole,
} from "../api";
import { when } from "../format";
import { Link } from "../router";
import { useSession } from "../session";
import { Table, td, th } from "../table";

const ROLES = Object.keys(ROLE_LABEL) as StaffRole[];
const AD_KINDS = Object.keys(AD_MODULE_LABEL) as AdModule[];

/** What a staff member may do, beyond their role: sections, kinds of ads, approving. */
export interface AccessForm {
  role: StaffRole;
  allSections: boolean; // everything the role allows
  modules: Module[];
  adModules: AdModule[]; // empty: all kinds
  canApprove: boolean;
  level: Level | null; // approvers check at one level
}

const fresh = (role: StaffRole = "moderator"): AccessForm => ({ role, allSections: true, modules: ROLE_MODULES[role], adModules: [], canApprove: true, level: "l1" });

function fromMember(s: StaffMember): AccessForm {
  return {
    role: s.staff_role,
    allSections: s.custom_modules === null,
    modules: s.custom_modules ?? ROLE_MODULES[s.staff_role],
    adModules: s.ad_modules ?? [],
    canApprove: s.can_approve || s.staff_role === "support",
    level: s.level ?? (s.can_approve ? "l1" : null),
  };
}

function body(a: AccessForm) {
  const allowed = ROLE_MODULES[a.role];
  return {
    role: a.role,
    modules: a.allSections ? null : a.modules.filter((m) => allowed.includes(m)),
    ad_modules: a.adModules.length ? a.adModules : null,
    can_approve: a.canApprove,
    level: a.canApprove && a.role !== "support" && a.role !== "super_admin" ? a.level : null,
  };
}

/** One line about a member's access, for the list. */
function summary(s: StaffMember): string {
  if (s.staff_role === "super_admin") return "Everything";
  const sections = s.custom_modules === null ? "All sections" : s.modules.map((m) => MODULE_LABEL[m]).join(", ") || "No sections";
  const ads = s.ad_modules ? s.ad_modules.map((m) => AD_MODULE_LABEL[m]).join(", ") : "All ads";
  const approve = s.staff_role === "support" ? "view only" : s.can_approve ? `approves at ${LEVEL_LABEL[s.level ?? "l1"].toLowerCase()}` : "cannot approve";
  return `${sections} · ${ads} · ${approve}`;
}

function AccessFields({ value, onChange, label }: { value: AccessForm; onChange: (a: AccessForm) => void; label: string }) {
  const allowed = ROLE_MODULES[value.role];
  const set = (patch: Partial<AccessForm>) => onChange({ ...value, ...patch });
  const toggle = <T,>(list: T[], item: T) => (list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);
  return (
    <div className="flex flex-col gap-3">
      <Label>
        Role
        <NativeSelect aria-label={`Role${label}`} value={value.role} onChange={(e) => set({ role: e.target.value as StaffRole })}>
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABEL[r]}
            </option>
          ))}
        </NativeSelect>
      </Label>
      {value.role === "super_admin" ? (
        <p className="m-0 text-sm text-muted-foreground">A super admin has every section, every kind of ad and approving, and manages staff.</p>
      ) : (
        <>
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-semibold">Sections</legend>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={value.allSections} onChange={(e) => set({ allSections: e.target.checked, modules: allowed })} /> All sections the role allows
            </label>
            {!value.allSections && (
              <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                {allowed.map((m) => (
                  <label key={m} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={value.modules.includes(m)} onChange={() => set({ modules: toggle(value.modules, m) })} /> {MODULE_LABEL[m]}
                  </label>
                ))}
              </div>
            )}
          </fieldset>
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-semibold">Ads they handle</legend>
            <ChipGroup role="group" aria-label={`Kinds of ads${label}`}>
              <Chip type="button" aria-pressed={value.adModules.length === 0} onClick={() => set({ adModules: [] })}>
                All
              </Chip>
              {AD_KINDS.map((k) => (
                <Chip type="button" key={k} aria-pressed={value.adModules.includes(k)} onClick={() => set({ adModules: toggle(value.adModules, k) })}>
                  {AD_MODULE_LABEL[k]}
                </Chip>
              ))}
            </ChipGroup>
          </fieldset>
          {value.role === "support" ? (
            <p className="m-0 text-sm text-muted-foreground">Support is view only: they never approve or reject.</p>
          ) : (
            <fieldset className="flex flex-col gap-1.5">
              <legend className="mb-1 text-sm font-semibold">Approving ads</legend>
              {(
                [
                  [null, "Cannot approve (looks only)"],
                  ["l1", "Level 1: first check"],
                  ["l2", "Level 2: second check, publishes"],
                ] as const
              ).map(([lv, text]) => (
                <label key={lv ?? "none"} className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name={`level${label}`}
                    checked={lv === null ? !value.canApprove : value.canApprove && value.level === lv}
                    onChange={() => set(lv === null ? { canApprove: false } : { canApprove: true, level: lv })}
                  />{" "}
                  {text}
                </label>
              ))}
              <span className="text-xs text-muted-foreground">Every ad and every change needs both levels, by two different people.</span>
            </fieldset>
          )}
        </>
      )}
    </div>
  );
}

/** Super admins add staff (an existing account, or a new one), set what each can do, and see what they did. */
export function Staff() {
  const { me } = useSession();
  const [staff, setStaff] = useState<StaffMember[] | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [access, setAccess] = useState<AccessForm>(fresh());
  const [editing, setEditing] = useState<{ id: string; access: AccessForm } | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [message, setMessage] = useState<{ kind: "ok" | "error"; text: string } | null>(null);

  const load = () =>
    api<StaffMember[]>("/admin/staff")
      .then(setStaff)
      .catch((err: Error) => setMessage({ kind: "error", text: err.message }));
  useEffect(() => {
    load();
  }, []);

  async function run<T>(step: () => Promise<T>, ok: string): Promise<T | undefined> {
    setMessage(null);
    try {
      const out = await step();
      setMessage({ kind: "ok", text: ok });
      await load();
      return out;
    } catch (err) {
      setMessage({ kind: "error", text: (err as Error).message });
    }
  }

  async function add(e: FormEvent) {
    e.preventDefault();
    setLink(null);
    const who = email.trim() || mobile.trim();
    const made = await run(
      () =>
        api<StaffMember>("/admin/staff", {
          method: "POST",
          body: { ...(name.trim() && { full_name: name.trim() }), ...(email.trim() && { email: email.trim() }), ...(mobile.trim() && { mobile: mobile.trim() }), ...body(access) },
        }),
      `${who} is now ${ROLE_LABEL[access.role]}`,
    );
    if (made) {
      setLink(made.set_password_link ?? null);
      setName("");
      setEmail("");
      setMobile("");
      setAccess(fresh());
    }
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!editing) return;
    const done = await run(() => api(`/admin/staff/${editing.id}`, { method: "PATCH", body: body(editing.access) }), "Access saved");
    if (done !== undefined) setEditing(null);
  }

  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Staff</h1>
      {message && <Alert tone={message.kind === "error" ? "error" : "success"}>{message.text}</Alert>}
      {link && (
        <Alert tone="success">
          <div className="flex flex-col gap-2">
            <span>Account made. Send this link to the person (it works once, for 3 days) so they set their password; then they log in here with email, password and an OTP to their mobile.</span>
            <code className="break-all rounded bg-muted px-2 py-1 text-xs">{link}</code>
            <Button type="button" className="self-start" onClick={() => navigator.clipboard?.writeText(link)}>
              Copy link
            </Button>
          </div>
        </Alert>
      )}
      <Card>
        <form className="flex flex-col gap-3" onSubmit={add} aria-label="Add staff">
          <CardTitle className="text-base">Add staff</CardTitle>
          <p className="m-0 text-sm text-muted-foreground">
            For someone with a RealRoof account, give their email or mobile. For a new person, give name, email and mobile: the account is made and you get a link for them to set a password.
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <Label>
              Name
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Only for a new account" />
            </Label>
            <Label>
              Email
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Label>
            <Label>
              Mobile
              <Input inputMode="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} />
            </Label>
          </div>
          <AccessFields value={access} onChange={setAccess} label="" />
          <Button type="submit" variant="primary" className="self-start" disabled={!email.trim() && !mobile.trim()}>
            Give access
          </Button>
        </form>
      </Card>
      {staff && (
        <Table>
          <thead>
            <tr>
              <th className={th}>Name</th>
              <th className={th}>Email / mobile</th>
              <th className={th}>Role and access</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {staff.map((s) => (
              <tr key={s.id} className="align-top">
                <td className={td}>{s.full_name || "—"}</td>
                <td className={td}>
                  {s.email || "—"}
                  <div className="text-xs text-muted-foreground">
                    {s.mobile || "no mobile"}
                    {!s.mobile_verified && " · mobile not verified (cannot log in)"}
                  </div>
                </td>
                <td className={td}>
                  {editing?.id === s.id ? (
                    <form className="flex flex-col gap-3" onSubmit={save} aria-label={`Access of ${s.email ?? s.mobile}`}>
                      <AccessFields value={editing.access} onChange={(a) => setEditing({ id: s.id, access: a })} label={` of ${s.email ?? s.mobile}`} />
                      <div className="flex gap-2">
                        <Button type="submit" variant="primary">
                          Save access
                        </Button>
                        <Button type="button" onClick={() => setEditing(null)}>
                          Cancel
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <strong>{ROLE_LABEL[s.staff_role]}</strong>
                      {s.id === me?.id && " (you)"}
                      <div className="text-xs text-muted-foreground">{summary(s)}</div>
                    </>
                  )}
                </td>
                <td className={td + " whitespace-nowrap"}>
                  <div className="flex flex-col items-start gap-1">
                    <Link to={`/staff/${s.id}`}>Activity</Link>
                    {s.id !== me?.id && editing?.id !== s.id && (
                      <Button variant="link" onClick={() => setEditing({ id: s.id, access: fromMember(s) })}>
                        Edit access
                      </Button>
                    )}
                    {s.id !== me?.id && (
                      <Button variant="link" className="text-destructive" onClick={() => window.confirm(`Remove staff access for ${s.email ?? s.mobile}?`) && run(() => api(`/admin/staff/${s.id}`, { method: "DELETE" }), "Access removed")}>
                        Remove access
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </section>
  );
}

const PERIODS = [
  [7, "7 days"],
  [30, "30 days"],
  [90, "90 days"],
  [366, "1 year"],
] as const;

/** Everything one staff member did: counts by action (ads approved, sent back ...) and the full list. */
export function StaffActivity({ id }: { id: string }) {
  const [days, setDays] = useState(30);
  const [action, setAction] = useState("");
  const [data, setData] = useState<Activity | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
    const params = new URLSearchParams({ days: String(days), limit: "500" });
    if (action) params.set("action", action);
    api<Activity>(`/admin/staff/${id}/activity?${params}`)
      .then(setData)
      .catch((err: Error) => setError(err.message));
  }, [id, days, action]);

  const m = data?.member;
  const counts = Object.entries(data?.counts ?? {}).sort((a, b) => b[1] - a[1]);
  return (
    <section className="flex flex-col gap-4">
      <Link to="/staff">← Staff</Link>
      <h1 className="text-2xl font-bold">{m ? `${m.full_name || m.email || m.mobile}: activity` : "Activity"}</h1>
      {error && <Alert tone="error">{error}</Alert>}
      {m && (
        <Card className="gap-1">
          <span>
            <strong>{ROLE_LABEL[m.staff_role]}</strong> · {m.email} · {m.mobile}
          </span>
          <span className="text-sm text-muted-foreground">{summary(m)}</span>
          <span className="text-sm text-muted-foreground">Last logged in: {when(data?.last_seen)}</span>
        </Card>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <ChipGroup role="group" aria-label="Period">
          {PERIODS.map(([d, label]) => (
            <Chip key={d} aria-pressed={d === days} onClick={() => setDays(d)}>
              Last {label}
            </Chip>
          ))}
        </ChipGroup>
      </div>
      {data && (
        <ChipGroup role="group" aria-label="What they did">
          <Chip aria-pressed={!action} onClick={() => setAction("")}>
            Everything
          </Chip>
          {counts.map(([a, n]) => (
            <Chip key={a} aria-pressed={a === action} onClick={() => setAction(a)}>
              {ACTION_LABEL[a] ?? a}
              <Badge className="ml-1.5">{n}</Badge>
            </Chip>
          ))}
        </ChipGroup>
      )}
      {data && counts.length === 0 && <p className="text-muted-foreground">Nothing in this period.</p>}
      {data && data.items.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th className={th}>When</th>
              <th className={th}>What</th>
              <th className={th}>On</th>
              <th className={th}>Details</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((r, i) => (
              <tr key={i}>
                <td className={td + " whitespace-nowrap"}>{when(r.created_at)}</td>
                <td className={td}>{ACTION_LABEL[r.action] ?? r.action}</td>
                <td className={td}>{r.ad_id ? <Link to={`/ads/${r.ad_id}`}>{r.ad}</Link> : r.target_type === "listing" ? "Ad (removed)" : r.target_type}</td>
                <td className={td + " text-sm text-muted-foreground"}>{detailText(r.detail)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </section>
  );
}
